/**
 * React Query hooks for the admin-only "Fias-owned plugins" section of the
 * contributor dashboard.
 *
 * Endpoints (all `requireAdmin`):
 *   GET    /admin/plugins/platform-owned
 *   GET    /admin/plugins/:archeId/maintainers
 *   POST   /admin/plugins/:archeId/maintainers        grant `publisher`
 *   DELETE /admin/plugins/:archeId/maintainers/:username
 *   GET    /admin/plugins/billing-account             who pays for Fias plugins
 *   PUT    /admin/plugins/billing-account             assign it
 *   GET    /admin/plugins/fias-arche-roles            which plugin each platform link opens
 *   PUT    /admin/plugins/fias-arche-roles/:role      assign or clear one
 *
 * The maintainer grant is the load-bearing one: ADR-019 derives a workspace
 * asset-library link's validity from "is some active member of this workspace an
 * active `can_publish` collaborator on this arche?", so taking a `publisher` row
 * is what lets an admin link a Fias-owned plugin to a shared library. That is
 * also why every mutation invalidates the arche's workspace-links key — the
 * grant flips `GET /contributor/arches/:id/workspaces` from 403 to 200.
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api, type FiasArcheRole, type PluginTeamRole } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export interface PlatformOwnedPluginsFilters {
  limit?: number;
  offset?: number;
}

/**
 * A page of Fias-owned plugin arches. Pagination only — see the route's schema
 * for why there is no text search.
 *
 * `enabled` is supplied by the caller's admin gate, so a non-admin never issues
 * the request at all (the server is still the authorization boundary).
 * `keepPreviousData` stops the table blanking while paging.
 */
export function usePlatformOwnedPlugins(
  filters: PlatformOwnedPluginsFilters,
  options?: { enabled?: boolean },
) {
  const { limit = 25, offset = 0 } = filters;
  return useQuery({
    queryKey: queryKeys.adminPlatformPlugins.list({ limit, offset }),
    queryFn: () => api.listPlatformOwnedPlugins({ limit, offset }),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

/**
 * The collaborator roster of one Fias-owned plugin. Fetched lazily — the UI only
 * needs it when confirming a self-revoke, to warn when the caller is the last
 * member who can publish (removing them invalidates every workspace link on the
 * plugin).
 */
export function usePluginMaintainers(archeId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.adminPlatformPlugins.maintainers(archeId),
    queryFn: () => api.listPluginMaintainers(archeId),
    enabled: (options?.enabled ?? true) && !!archeId,
  });
}

/**
 * Invalidates everything a maintainer change affects: the admin list (whose
 * `youAreMaintainer` flag and link validity both move), that plugin's roster,
 * and the arche-side workspace-links query on the contributor arche page.
 *
 * Exported so the Dev Console team panel can also refresh on a 409 — the
 * signal that the roster moved under the admin — without owning a QueryClient
 * of its own.
 */
export function useInvalidateMaintainerState(archeId: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.adminPlatformPlugins.all });
    void queryClient.invalidateQueries({
      queryKey: queryKeys.contributorArches.workspaces(archeId),
    });
  };
}

/** Grants `publisher` on a Fias-owned plugin to the named account. */
export function useAddPluginMaintainer(archeId: string) {
  const invalidate = useInvalidateMaintainerState(archeId);
  return useMutation({
    mutationFn: (username: string) => api.addPluginMaintainer(archeId, username),
    onSuccess: invalidate,
  });
}

/** Revokes a maintainer. Idempotent; the platform owner row is protected server-side. */
export function useRemovePluginMaintainer(archeId: string) {
  const invalidate = useInvalidateMaintainerState(archeId);
  return useMutation({
    mutationFn: (username: string) => api.removePluginMaintainer(archeId, username),
    onSuccess: invalidate,
  });
}

// ============================================================================
// Dev Console — Fias Plugins (annex + role-aware team)
// ============================================================================

/**
 * Upper bound on the annex picker. Fias-owned rows are excluded in SQL, so this
 * is a page over ELIGIBLE plugins; the card says so when the page is full.
 */
export const ANNEX_CANDIDATE_LIMIT = 200;

/**
 * Developer plugins eligible for annexation: the admin arches-summary listing
 * restricted to `kind: 'plugin'` with Fias-owned rows excluded server-side
 * (`excludePlatformOwned`), so the limit applies to annexable rows rather than
 * to a window that Fias-owned rows could crowd out. The server's tri-state
 * `isPlatformOwned` is still honoured on the client for the "could not verify"
 * case.
 */
export function useAnnexCandidates(search: string, options?: { enabled?: boolean }) {
  const trimmed = search.trim();
  return useQuery({
    queryKey: queryKeys.adminPlatformPlugins.annexCandidates(trimmed),
    queryFn: () =>
      api.adminGetArchesSummary({
        kind: 'plugin',
        lifecycle: 'active',
        excludePlatformOwned: true,
        limit: ANNEX_CANDIDATE_LIMIT,
        search: trimmed || undefined,
      }),
    select: (data) => data.arches,
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

/**
 * Annex one developer plugin into Fias ownership. On success the plugin moves
 * from the candidate list to the Fias-owned list, so both are under the same
 * key prefix and one invalidation refreshes them together.
 */
export function useAnnexPlugin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { archeId: string; reason: string; consentRef?: string }) =>
      api.annexPlugin(input.archeId, {
        reason: input.reason,
        ...(input.consentRef ? { consentRef: input.consentRef } : {}),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminPlatformPlugins.all });
    },
  });
}

/** Set Read or Publish for one account on a Fias-owned plugin (upsert). */
export function useSetPluginTeamMember(archeId: string) {
  const invalidate = useInvalidateMaintainerState(archeId);
  return useMutation({
    mutationFn: (input: { identifier: string; role: PluginTeamRole }) =>
      api.setPluginTeamMember(archeId, input),
    onSuccess: invalidate,
  });
}

/**
 * Revoke a collaborator row by its stored user id. Distinct from
 * `useRemovePluginMaintainer` (the gated maintainers route, which re-resolves
 * the identifier through the users table): this addresses the row itself, so
 * it works for an account whose profile is gone.
 */
export function useRemovePluginTeamMember(archeId: string) {
  const invalidate = useInvalidateMaintainerState(archeId);
  return useMutation({
    mutationFn: (userId: string) => api.removePluginTeamMember(archeId, userId),
    onSuccess: invalidate,
  });
}

/** The account that pays for every Fias-owned plugin. */
export function useFiasPluginBillingAccount(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.adminPlatformPlugins.billingAccount(),
    queryFn: async () => (await api.getFiasPluginBillingAccount()).billingAccount,
    enabled: options?.enabled ?? true,
  });
}

/** Assign the Fias plugin billing account (user id, username or email). */
export function useSetFiasPluginBillingAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (identifier: string) => api.setFiasPluginBillingAccount(identifier),
    onSuccess: (result) => {
      queryClient.setQueryData(
        queryKeys.adminPlatformPlugins.billingAccount(),
        result.billingAccount,
      );
    },
  });
}

// ============================================================================
// Dev Console — Fias arche roles (ADR-037 D8)
// ============================================================================

/** Which Fias plugin the platform links to for each job, with that arche. */
export function useFiasArcheRoles(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.adminPlatformPlugins.fiasArcheRoles(),
    queryFn: async () => (await api.listFiasArcheRoles()).roles,
    enabled: options?.enabled ?? true,
  });
}

/** Point a role at a Fias-owned plugin, or clear it with null. */
export function useSetFiasArcheRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ role, archeId }: { role: FiasArcheRole; archeId: string | null }) =>
      api.setFiasArcheRole(role, archeId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.adminPlatformPlugins.fiasArcheRoles(),
      });
      // Every link on the platform follows the new answer.
      void queryClient.invalidateQueries({ queryKey: queryKeys.fiasArches.all });
    },
  });
}
